/**
 * HEIC/HEIF Conversion Utility
 * 
 * Converts HEIC/HEIF images to JPEG for browser compatibility and backend upload.
 */

import { getAttachmentFileType, normalizeUploadMimeType, wrapUploadFileWithMime } from './attachmentHelpers';

/**
 * Check if HEIC conversion is available.
 * @returns {boolean}
 */
export function isHeicConversionAvailable() {
  try {
    return typeof window !== 'undefined';
  } catch {
    return false;
  }
}

/** Replace HEIC/HEIF or other image extension with .jpg for backend upload. */
export function toJpegFilename(name) {
  const base = String(name || 'document').replace(/\.[^/.]+$/, '');
  return `${base}.jpg`;
}

/**
 * Converts a HEIC/HEIF file to JPEG.
 * 
 * @param {File} heicFile - The HEIC/HEIF file
 * @returns {Promise<{blob: Blob, url: string, originalName: string}>}
 */
export async function convertHeicToJpeg(heicFile) {
  try {
    const heic2any = (await import('heic2any')).default;

    const jpegBlob = await heic2any({
      blob: heicFile,
      toType: 'image/jpeg',
      quality: 0.92,
    });

    const blob = Array.isArray(jpegBlob) ? jpegBlob[0] : jpegBlob;
    const url = URL.createObjectURL(blob);

    return {
      blob,
      url,
      originalName: toJpegFilename(heicFile.name),
    };
  } catch (error) {
    console.error('HEIC conversion error:', error);
    throw new Error('This HEIC image could not be converted. Please try saving it as JPG or PNG and upload again.');
  }
}

/**
 * Ensures a File is JPEG with a .jpg filename before backend upload.
 * Converts HEIC/HEIF; re-wraps JPEG blobs that still carry HEIC names or MIME types.
 *
 * @param {File|Blob} file
 * @returns {Promise<File>}
 */
export async function ensureJpegUploadFile(file) {
  if (!file) {
    throw new Error('No file provided for upload');
  }

  const normalized = wrapUploadFileWithMime(file);
  const fileType = getAttachmentFileType(normalized);
  if (fileType === 'heic') {
    const converted = await convertHeicToJpeg(normalized);
    return new File([converted.blob], converted.originalName, {
      type: 'image/jpeg',
      lastModified: normalized.lastModified || Date.now(),
    });
  }

  const name = normalized.name || 'document.jpg';
  const normalizedMime = normalizeUploadMimeType(normalized);
  const isJpegMime = normalizedMime === 'image/jpeg' || normalizedMime === 'image/jpg';
  const hasJpegExt = /\.jpe?g$/i.test(name);

  if (isJpegMime || hasJpegExt) {
    const jpegName = hasJpegExt ? name : toJpegFilename(name);
    if (normalized instanceof File && normalized.type === 'image/jpeg' && normalized.name === jpegName) {
      return normalized;
    }
    return new File([normalized], jpegName, {
      type: 'image/jpeg',
      lastModified: normalized.lastModified || Date.now(),
    });
  }

  if (fileType === 'image' && normalizedMime === 'image/png') {
    if (normalized instanceof File && normalized.type === 'image/png') {
      return normalized;
    }
    return new File([normalized], name, {
      type: 'image/png',
      lastModified: normalized.lastModified || Date.now(),
    });
  }

  return normalized instanceof File
    ? normalized
    : new File([normalized], name, { type: normalizedMime || 'application/octet-stream' });
}

/**
 * Checks if a file is HEIC/HEIF based on type or extension.
 * 
 * @param {File} file - The file to check
 * @returns {boolean}
 */
export function isHeicFile(file) {
  if (!file) return false;
  return getAttachmentFileType(file) === 'heic';
}

export default {
  isHeicConversionAvailable,
  convertHeicToJpeg,
  ensureJpegUploadFile,
  toJpegFilename,
  isHeicFile,
};
